/* eslint-disable max-len */
import _ from 'lodash';
import React, { useEffect, useCallback, useContext } from 'react';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import { useValidation } from 'gw-portals-validation-react';
import { TranslatorContext } from '@jutro/locale';
import FNOLRepairEstimatesWrapper from '../estimatesComponents/FNOLRepairEstimatesWrapper/FNOLRepairEstimatesWrapper';
import metadata from './WizardFNOLPropertyRepair.metadata.json5';
import styles from './WizardFNOLPropertyRepair.module.scss';
import messages from './WizardFNOLPropertyRepair.messages';

function WizardFNOLPropertyRepair(props) {
    const {
        id,
        value: repairDTO,
        wizardDTO,
        onValidate,
        writeValue,
        showRepairInKind,
        showRepairNoVendor,
        path
    } = props;
    const translator = useContext(TranslatorContext);

    const {
        onValidate: onComponentValidate,
        isComponentValid,
        disregardFieldValidation
    } = useValidation('WizardFNOLPropertyRepair');

    useEffect(() => {
        if (onValidate) {
            onValidate(isComponentValid, id);
        }
    }, [id, isComponentValid, onValidate]);

    const handleValueChange = useCallback((value, changedPath) => {
        const fullPath = `${path}.${changedPath}`;
        writeValue(value, fullPath);
    }, [writeValue, path]);

    const handleValueChangeWithReset = useCallback((value, changedPath) => {
        handleValueChange(value, changedPath);
        const fullPath = `${path}.${changedPath}`;
        if (fullPath === 'repairDetails.alreadyReceivedEstimate' && value === false) {
            writeValue(undefined, 'repairDetails.detailedContentEstimates');
            writeValue(undefined, 'repairDetails.detailedContentEstimatesPresent');
            writeValue(undefined, 'repairDetails.isFinalEstimate');

            disregardFieldValidation(['fNOLRepairEstimatesContentSection']);
        }
    }, [writeValue, path]);

    const renderSecondaryLabelPreferredVendor = useCallback(
        () => {
            return (
                <React.Fragment>
                    <div>{translator(messages.ownVendorSecondaryLabelHeader)}</div>
                    <ol>
                        <li>{translator(messages.ownVendorSecondaryLabelContractorClause)}</li>
                        <li>{translator(messages.ownVendorSecondaryLabelQuickRepairClause)}</li>
                        <li>{translator(messages.ownVendorSecondaryLabelQuotationsClause)}</li>
                        <li>{translator(messages.ownVendorSecondaryLabelExemptionClause)}</li>
                        <li>{translator(messages.ownVendorSecondaryLabelWeArrangeEverythingClause)}</li>
                        <li>{translator(messages.ownVendorSecondaryLabelQualityClause)}</li>
                        <li>{translator(messages.ownVendorSecondaryLabelWarrantyClause)}</li>
                    </ol>
                </React.Fragment>
            );
        }, [translator]
    );

    const onValueChangeWithRepairDetailsReset = useCallback((value, changedPath) => {
        handleValueChange(null, 'alreadyReceivedEstimate');
        handleValueChange(null, 'damageAlreadyRepaired');
        handleValueChange({}, 'estimatedRepairCost');
        handleValueChange({}, 'repairFacilityAddress');
        handleValueChange(null, 'repairFacilityName');
        handleValueChange(value, changedPath);
        if (value !== 'NewVendor') {
            disregardFieldValidation(['FNOLRepairEstimates', 'fNOLRepairEstimatesReceived']);
        }
    }, [handleValueChange, disregardFieldValidation]);


    function isOnlyGlassHobOrGreenHouseSelected() {
        const allowed = ['glass_hob', 'green_house'];
        const selected = [];
        let isSelected = false;
        wizardDTO.propertyDamage.value.forEach(group => {
            group.selectedDetailDamages.forEach(d => {
                if (d.isSelected) {
                    selected.push(d.damageDetail);
                }
            });
        });
        if (selected.length === 0) return false;
        // If only one is selected and it's allowed
        if (selected.length === 1) {
            if (allowed.includes(selected[0])) {
                repairDTO.repairOptionChoice.value = 'NewVendor';
                isSelected = true;
            } else {
                isSelected = false;
            }
        }
        // If two or more are selected
        const allAllowed = selected.every(d => allowed.includes(d));
        if (allAllowed) {
            repairDTO.repairOptionChoice.value = 'NewVendor';
            isSelected = true;
        } else {
            isSelected = false;
        }
        return isSelected;
    }

    const overrideProps = {
        wizardFNOLPropertyRepairHeaderFirstText: {
            visible: showRepairInKind
        },
        wizardFNOLPropertyRepairHeaderSecondText: {
            visible: showRepairInKind
        },
        wizardFNOLPropertyRepairChoicePreferredVendor: {
            value: repairDTO.repairOptionChoice.value.code,
            renderSecondaryLabel: renderSecondaryLabelPreferredVendor,
            onValueChange: onValueChangeWithRepairDetailsReset,
            visible: showRepairInKind && !isOnlyGlassHobOrGreenHouseSelected()
        },
        wizardFNOLPropertyRepairChoiceOwnVendor: {
            value: repairDTO.repairOptionChoice.value.code,
            onValueChange: onValueChangeWithRepairDetailsReset,
            availableValues: [{
                id: 'newVendor',
                code: 'NewVendor',
                displayName: showRepairInKind ? messages.wizardFNOLPropertyRepairChoiceOwnVendor : messages.wizardFNOLPropertyRepairChoiceOwnVendorNoRepairInKind
            }]
        },
        wizardFNOLPropertyRepairChoiceOwnVendorContent: {
            visible: repairDTO.repairOptionChoice.value.code === 'NewVendor'
        },
        wizardFNOLPropertyRepairNoVendorChoice: {
            value: repairDTO.repairOptionChoice.value.code,
            onValueChange: onValueChangeWithRepairDetailsReset,
            visible: showRepairNoVendor
        },
        wizardFNOLPropertyRepairChoiceNoVendorContent: {
            visible: repairDTO.repairOptionChoice.value.code === 'NoRepair'
        },
        fNOLRepairEstimatesReceived: {
            writeValue: handleValueChangeWithReset,
            repairDTO: repairDTO,
            wizardDTO: wizardDTO,
            showContentEstimateSection: wizardDTO.propertyDamage.value.find((propertyDamageElement) => {
                return propertyDamageElement.isSelected === true && ['possession', 'garden_furniture'].indexOf(propertyDamageElement.damagedProperty) !== -1;
            }) !== undefined,
            showBuildingEstimateSection: wizardDTO.propertyDamage.value.find((propertyDamageElement) => {
                return propertyDamageElement.isSelected === true && ['building', 'other_structures', 'garden'].indexOf(propertyDamageElement.damagedProperty) !== -1;
            }) !== undefined
        }
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveComponentMap: {
            estimatesReceivedWrapperComponent: FNOLRepairEstimatesWrapper,
        },
        resolveCallbackMap: {
            onValidate: onComponentValidate
        }
    };

    return (
        <div>
            <ViewModelForm
                uiProps={metadata.componentContent}
                model={repairDTO}
                overrideProps={overrideProps}
                classNameMap={resolvers.resolveClassNameMap}
                componentMap={resolvers.resolveComponentMap}
                callbackMap={resolvers.resolveCallbackMap}
                onValidationChange={onComponentValidate}
                onValueChange={handleValueChange}
            />
        </div>
    );
}

export default WizardFNOLPropertyRepair;
